"use client";

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import AlertDialog from "./AlertDialog";
import {
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  StarIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1e2430",
    borderColor: state.isFocused ? "#f97316" : "#2d3748",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    borderRadius: "0.5rem",
    padding: "0.1rem 0.25rem",
    minHeight: "38px",
    fontSize: "13px",
    color: "#e2e8f0",
  }),
  input: (p) => ({ ...p, color: "#e2e8f0", margin: 0, padding: 0 }),
  singleValue: (p) => ({ ...p, color: "#e2e8f0" }),
  placeholder: (p) => ({ ...p, color: "#64748b" }),
  option: (p, state) => ({
    ...p,
    backgroundColor: state.isSelected ? "#2d3748" : "#1e2430",
    color: state.isSelected ? "#f97316" : "#e2e8f0",
    "&:hover": { backgroundColor: "#2d3748" },
    fontSize: "13px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#1e2430",
    borderRadius: "0.5rem",
    border: "1px solid #2d3748",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
  }),
  valueContainer: (b) => ({ ...b, padding: "0 6px" }),
  indicatorsContainer: (b) => ({ ...b, padding: "0" }),
  dropdownIndicator: (b) => ({
    ...b,
    padding: "4px",
    color: "#64748b",
    "&:hover": { color: "#e2e8f0" },
  }),
  clearIndicator: (b) => ({
    ...b,
    color: "#64748b",
    "&:hover": { color: "#f97316" },
  }),
};

const INPUT_CLS =
  "w-full rounded-lg bg-[#1e2430] border border-[#2d3748] text-slate-200 text-sm px-3 py-2 placeholder-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const MAPS_KEY = "AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg";

function AccountAvatar({ companyName }) {
  const initials = (companyName || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const colors = [
    "#b45309",
    "#0f766e",
    "#1d4ed8",
    "#7c3aed",
    "#be123c",
    "#0369a1",
  ];
  const idx = (companyName?.charCodeAt(0) || 0) % colors.length;
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
      style={{ backgroundColor: colors[idx] }}
    >
      {initials}
    </div>
  );
}

function emptyForm() {
  return {
    companyName: "",
    aliases: [],
    address: {
      formattedAddress: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      latitude: null,
      longitude: null,
    },
    type: "vendor",
  };
}

function emptyBranchForm() {
  return {
    name: "",
    address: {
      formattedAddress: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      latitude: null,
      longitude: null,
    },
    isPrimary: false,
  };
}

async function geocodePlaceId(placeId, label) {
  const res = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${MAPS_KEY}`,
  );
  const json = await res.json();
  if (json.status !== "OK" || !json.results.length) return null;
  const result = json.results[0];
  const { lat, lng } = result.geometry.location;
  let street = "",
    city = "",
    state = "",
    postalCode = "",
    country = "";
  result.address_components.forEach((c) => {
    if (c.types.includes("street_number") || c.types.includes("route"))
      street += (street ? " " : "") + c.long_name;
    if (c.types.includes("locality")) city = c.long_name;
    if (c.types.includes("administrative_area_level_1")) state = c.short_name;
    if (c.types.includes("postal_code")) postalCode = c.long_name;
    if (c.types.includes("country")) country = c.long_name;
  });
  return {
    formattedAddress: label,
    street,
    city,
    state,
    postalCode,
    country,
    latitude: lat,
    longitude: lng,
  };
}

export default function AccountsView() {
  const [accounts, setAccounts] = useState([]);
  const [archivedAccounts, setArchivedAccounts] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [isAdding, setIsAdding] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });
  const [aliasInput, setAliasInput] = useState("");
  const [relatedContacts, setRelatedContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const formRef = useRef(null);

  // Branch state
  const [branches, setBranches] = useState([]);
  const [branchForm, setBranchForm] = useState(null); // null=closed, obj=open
  const [editingBranchId, setEditingBranchId] = useState(null);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await fetchAccounts(showArchived);
      setIsLoading(false);
    })();
  }, [showArchived]);

  const token = () => localStorage.getItem("token");
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const fetchAccounts = async (includeArchived = false) => {
    try {
      const url = includeArchived
        ? `${API}/accounts/archived`
        : `${API}/accounts`;
      const { data } = await axios.get(url, { headers: authHeader() });
      includeArchived ? setArchivedAccounts(data) : setAccounts(data);
    } catch {
      showAlert("error", "Error", "Failed to load accounts.");
    }
  };

  const fetchRelatedContacts = async (accountId) => {
    try {
      const { data } = await axios.get(
        `${API}/contacts?relatedAccount=${accountId}`,
        {
          headers: authHeader(),
        },
      );
      setRelatedContacts(data);
    } catch {
      showAlert("error", "Error", "Failed to load related contacts.");
    }
  };

  const fetchBranches = async (accountId) => {
    try {
      const { data } = await axios.get(
        `${API}/accounts/${accountId}/branches`,
        {
          headers: authHeader(),
        },
      );
      setBranches(data);
    } catch {
      showAlert("error", "Error", "Failed to load branches.");
    }
  };

  const showAlert = (type, title, message) =>
    setAlert({ open: true, type, title, message });
  const closeAlert = () => setAlert((p) => ({ ...p, open: false }));

  const handleArchive = async (accountId, companyName) => {
    if (!window.confirm(`Archive "${companyName}"?`)) return;
    try {
      await axios.delete(`${API}/accounts/${accountId}/archive`, {
        headers: authHeader(),
      });
      showAlert("success", "Archived", `"${companyName}" archived.`);
      fetchAccounts(showArchived);
    } catch {
      showAlert("error", "Error", "Failed to archive account.");
    }
  };

  const handleRestore = async (accountId, companyName) => {
    if (!window.confirm(`Restore "${companyName}"?`)) return;
    try {
      await axios.post(
        `${API}/accounts/${accountId}/restore`,
        {},
        { headers: authHeader() },
      );
      showAlert("success", "Restored", `"${companyName}" restored.`);
      fetchAccounts(showArchived);
    } catch {
      showAlert("error", "Error", "Failed to restore account.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setFormData((p) => ({ ...p, address: { ...p.address, [field]: value } }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const handlePlaceSelect = async (place) => {
    if (!place?.value) return;
    try {
      const addr = await geocodePlaceId(place.value.place_id, place.label);
      if (addr) setFormData((p) => ({ ...p, address: addr }));
    } catch {
      showAlert("error", "Error", "Error fetching address details.");
    }
  };

  // ── Alias helpers ───────────────────────────────────────────────
  const addAlias = () => {
    const val = aliasInput.trim();
    if (!val) return;
    const exists = (formData.aliases || []).some(
      (a) => a.toLowerCase() === val.toLowerCase(),
    );
    if (!exists) {
      setFormData((p) => ({ ...p, aliases: [...(p.aliases || []), val] }));
    }
    setAliasInput("");
  };

  const removeAlias = (alias) => {
    setFormData((p) => ({
      ...p,
      aliases: (p.aliases || []).filter((a) => a !== alias),
    }));
  };

  const handleAliasKeyDown = (e) => {
    // Enter or comma commits the chip; keep it out of the outer form submit
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addAlias();
    } else if (
      e.key === "Backspace" &&
      !aliasInput &&
      formData.aliases?.length
    ) {
      // backspace on empty input pops the last chip
      setFormData((p) => ({ ...p, aliases: p.aliases.slice(0, -1) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await axios.put(`${API}/accounts/${editingAccount._id}`, formData, {
          headers: authHeader(),
        });
        setEditingAccount(null);
        setRelatedContacts([]);
        setBranches([]);
        showAlert("success", "Saved", "Account updated.");
      } else {
        await axios.post(`${API}/accounts`, formData, {
          headers: authHeader(),
        });
        setIsAdding(false);
        showAlert("success", "Created", "Account added.");
      }
      setFormData(emptyForm());
      fetchAccounts(showArchived);
      setCurrentPage(1);
    } catch {
      showAlert("error", "Error", "Failed to save account.");
    }
  };

  const editAccount = (account) => {
    setEditingAccount(account);
    setIsAdding(false);
    setBranchForm(null);
    setEditingBranchId(null);
    setAliasInput("");
    setFormData({
      companyName: account.companyName,
      aliases: account.aliases || [],
      address: {
        formattedAddress:
          account.address?.formattedAddress ||
          `${account.address?.street || ""}, ${account.address?.city || ""}, ${account.address?.state || ""} ${account.address?.postalCode || ""}, ${account.address?.country || ""}`.trim(),
        street: account.address?.street || "",
        city: account.address?.city || "",
        state: account.address?.state || "",
        postalCode: account.address?.postalCode || "",
        country: account.address?.country || "",
        latitude: account.address?.latitude || null,
        longitude: account.address?.longitude || null,
      },
      type: account.type,
    });
    fetchRelatedContacts(account._id);
    fetchBranches(account._id);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };

  const openAdd = () => {
    setIsAdding(true);
    setEditingAccount(null);
    setFormData(emptyForm());
    setRelatedContacts([]);
    setBranches([]);
    setBranchForm(null);
    setEditingBranchId(null);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };

  const cancelForm = () => {
    setIsAdding(false);
    setEditingAccount(null);
    setRelatedContacts([]);
    setBranches([]);
    setBranchForm(null);
    setEditingBranchId(null);
  };

  // ── Branch helpers ──────────────────────────────────────────────

  const openNewBranch = () => {
    setEditingBranchId(null);
    setBranchForm(emptyBranchForm());
  };

  const openEditBranch = (branch) => {
    setEditingBranchId(branch._id);
    setBranchForm({
      name: branch.name,
      address: { ...branch.address },
      isPrimary: branch.isPrimary || false,
    });
  };

  const cancelBranchForm = () => {
    setBranchForm(null);
    setEditingBranchId(null);
  };

  const handleBranchInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setBranchForm((p) => ({
        ...p,
        address: { ...p.address, [field]: value },
      }));
    } else {
      setBranchForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleBranchPlaceSelect = async (place) => {
    if (!place?.value) return;
    try {
      const addr = await geocodePlaceId(place.value.place_id, place.label);
      if (addr) setBranchForm((p) => ({ ...p, address: addr }));
    } catch {
      showAlert("error", "Error", "Error fetching branch address.");
    }
  };

  const saveBranch = async () => {
    if (!branchForm?.name?.trim()) {
      showAlert("error", "Validation", "Branch name is required.");
      return;
    }
    try {
      if (editingBranchId) {
        await axios.put(
          `${API}/accounts/${editingAccount._id}/branches/${editingBranchId}`,
          branchForm,
          { headers: authHeader() },
        );
      } else {
        await axios.post(
          `${API}/accounts/${editingAccount._id}/branches`,
          branchForm,
          { headers: authHeader() },
        );
      }
      await fetchBranches(editingAccount._id);
      setBranchForm(null);
      setEditingBranchId(null);
    } catch {
      showAlert("error", "Error", "Failed to save branch.");
    }
  };

  const deleteBranch = async (branchId) => {
    if (!window.confirm("Delete this branch location?")) return;
    try {
      await axios.delete(
        `${API}/accounts/${editingAccount._id}/branches/${branchId}`,
        { headers: authHeader() },
      );
      await fetchBranches(editingAccount._id);
    } catch {
      showAlert("error", "Error", "Failed to delete branch.");
    }
  };

  const setPrimaryBranch = async (branchId) => {
    try {
      await axios.put(
        `${API}/accounts/${editingAccount._id}/branches/${branchId}`,
        { isPrimary: true },
        { headers: authHeader() },
      );
      await fetchBranches(editingAccount._id);
    } catch {
      showAlert("error", "Error", "Failed to update primary branch.");
    }
  };

  // ── Filtering / pagination ──────────────────────────────────────

  const displayList = showArchived ? archivedAccounts : accounts;

  const filteredAccounts = displayList.filter((a) => {
    const matchesSearch = a.companyName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || a.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentAccounts = filteredAccounts.slice(
    indexOfFirst,
    indexOfFirst + itemsPerPage,
  );
  const paginate = (n) => {
    if (n >= 1 && n <= totalPages) setCurrentPage(n);
  };

  const showForm = isAdding || !!editingAccount;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading accounts…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-slate-200 p-6 space-y-6">
      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-orange-500 tracking-tight">
            Accounts
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {displayList.length} {showArchived ? "archived" : "active"} accounts
          </p>
        </div>
        {!showForm && (
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-400 text-white rounded-lg text-sm font-medium transition shadow-lg shadow-orange-900/30"
          >
            <PlusIcon className="w-4 h-4" />
            Add Account
          </button>
        )}
      </div>

      {/* ── FORM PANEL ── */}
      {showForm && (
        <div
          ref={formRef}
          className="rounded-xl border border-[#2d3748] bg-[#161b27] shadow-2xl overflow-hidden"
        >
          {/* Form header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#2d3748] bg-[#1a2035]">
            <h2 className="text-base font-semibold text-white">
              {editingAccount ? "Edit Account" : "New Account"}
            </h2>
            <button
              onClick={cancelForm}
              className="text-slate-500 hover:text-white transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Company Name + Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Company Name
                </label>
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Acme Corp"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={INPUT_CLS}
                >
                  <option value="vendor">Vendor</option>
                  <option value="account">Account</option>
                </select>
              </div>
            </div>

            {/* Aliases */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Aliases
                <span className="ml-2 normal-case tracking-normal text-slate-500 font-normal">
                  shorthand names used in uploads (e.g. "Watson", "FBM")
                </span>
              </label>
              <div className="flex flex-wrap gap-2 rounded-lg bg-[#1e2430] border border-[#2d3748] p-2 focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500 transition">
                {(formData.aliases || []).map((alias) => (
                  <span
                    key={alias}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#2d3748] text-slate-200 text-xs"
                  >
                    {alias}
                    <button
                      type="button"
                      onClick={() => removeAlias(alias)}
                      className="text-slate-500 hover:text-red-400 transition"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={handleAliasKeyDown}
                  onBlur={addAlias}
                  placeholder={
                    formData.aliases?.length
                      ? "Add another…"
                      : "Type an alias, press Enter"
                  }
                  className="flex-1 min-w-[120px] bg-transparent text-slate-200 text-sm px-1 py-0.5 placeholder-slate-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Primary address label */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Primary Address
              </span>
              <div className="flex-1 h-px bg-[#2d3748]" />
            </div>

            {/* Address autocomplete */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-500">Address Lookup</label>
              <GooglePlacesAutocomplete
                autocompletionRequest={{
                  componentRestrictions: { country: ["ca"] },
                }}
                apiKey={MAPS_KEY}
                selectProps={{
                  onChange: handlePlaceSelect,
                  placeholder: "Search address…",
                  value: formData.address.formattedAddress
                    ? {
                        label: formData.address.formattedAddress,
                        value: formData.address.formattedAddress,
                      }
                    : null,
                  styles: selectStyles,
                }}
              />
            </div>

            {/* Address fields */}
            <div className="grid grid-cols-1 gap-3 pl-3 border-l-2 border-[#2d3748]">
              <input
                className={INPUT_CLS}
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleInputChange}
                placeholder="Street"
              />
              <div className="grid grid-cols-3 gap-3">
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  placeholder="City"
                />
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleInputChange}
                  placeholder="Province"
                />
                <input
                  className={INPUT_CLS}
                  type="text"
                  name="address.postalCode"
                  value={formData.address.postalCode}
                  onChange={handleInputChange}
                  placeholder="Postal code"
                />
              </div>
              <input
                className={INPUT_CLS}
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleInputChange}
                placeholder="Country"
              />
            </div>

            {/* ── BRANCHES (edit mode only) ── */}
            {editingAccount && (
              <div className="space-y-3">
                {/* Section header */}
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Branch Locations
                  </span>
                  <div className="flex-1 h-px bg-[#2d3748]" />
                  {!branchForm && (
                    <button
                      type="button"
                      onClick={openNewBranch}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1e2430] border border-[#2d3748] text-slate-400 hover:text-orange-400 hover:border-orange-600 text-xs transition"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      Add Branch
                    </button>
                  )}
                </div>

                {/* Inline branch form */}
                {branchForm && (
                  <div className="rounded-lg border border-orange-500/30 bg-[#1a2035] p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-orange-400">
                        {editingBranchId ? "Edit Branch" : "New Branch"}
                      </span>
                      <button
                        type="button"
                        onClick={cancelBranchForm}
                        className="text-slate-500 hover:text-white transition"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Branch name + primary toggle */}
                    <div className="grid grid-cols-2 gap-3 items-end">
                      <div className="space-y-1.5">
                        <label className="text-xs text-slate-500">
                          Branch Name *
                        </label>
                        <input
                          className={INPUT_CLS}
                          type="text"
                          name="name"
                          value={branchForm.name}
                          onChange={handleBranchInputChange}
                          placeholder='e.g. "Main Office", "Warehouse #2"'
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer pb-2 select-none">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={branchForm.isPrimary}
                            onChange={(e) =>
                              setBranchForm((p) => ({
                                ...p,
                                isPrimary: e.target.checked,
                              }))
                            }
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-[#2d3748] peer-focus:ring-2 peer-focus:ring-orange-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500" />
                        </div>
                        <span className="text-xs text-slate-400">
                          Mark as primary
                        </span>
                      </label>
                    </div>

                    {/* Branch address lookup */}
                    <div className="space-y-1.5">
                      <label className="text-xs text-slate-500">
                        Address Lookup
                      </label>
                      <GooglePlacesAutocomplete
                        autocompletionRequest={{
                          componentRestrictions: { country: ["ca"] },
                        }}
                        apiKey={MAPS_KEY}
                        selectProps={{
                          onChange: handleBranchPlaceSelect,
                          placeholder: "Search branch address…",
                          value: branchForm.address?.formattedAddress
                            ? {
                                label: branchForm.address.formattedAddress,
                                value: branchForm.address.formattedAddress,
                              }
                            : null,
                          styles: selectStyles,
                        }}
                      />
                    </div>

                    {/* Branch address fields */}
                    <div className="grid grid-cols-1 gap-2 pl-3 border-l-2 border-[#2d3748]">
                      <input
                        className={INPUT_CLS}
                        type="text"
                        name="address.street"
                        value={branchForm.address?.street || ""}
                        onChange={handleBranchInputChange}
                        placeholder="Street"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          className={INPUT_CLS}
                          type="text"
                          name="address.city"
                          value={branchForm.address?.city || ""}
                          onChange={handleBranchInputChange}
                          placeholder="City"
                        />
                        <input
                          className={INPUT_CLS}
                          type="text"
                          name="address.state"
                          value={branchForm.address?.state || ""}
                          onChange={handleBranchInputChange}
                          placeholder="Province"
                        />
                        <input
                          className={INPUT_CLS}
                          type="text"
                          name="address.postalCode"
                          value={branchForm.address?.postalCode || ""}
                          onChange={handleBranchInputChange}
                          placeholder="Postal code"
                        />
                      </div>
                      <input
                        className={INPUT_CLS}
                        type="text"
                        name="address.country"
                        value={branchForm.address?.country || ""}
                        onChange={handleBranchInputChange}
                        placeholder="Country"
                      />
                    </div>

                    {/* Branch form actions */}
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={cancelBranchForm}
                        className="px-3 py-1.5 text-xs rounded-lg bg-[#2d3748] text-slate-300 hover:bg-[#374151] transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveBranch}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-500 hover:bg-orange-400 text-white transition"
                      >
                        {editingBranchId ? "Update Branch" : "Save Branch"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Branch list */}
                {branches.length === 0 && !branchForm ? (
                  <p className="text-slate-600 text-sm italic">
                    No branch locations added yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {branches.map((branch) => (
                      <div
                        key={branch._id}
                        className={`rounded-lg border p-3 flex items-start justify-between gap-3 ${
                          branch.isPrimary
                            ? "border-orange-500/40 bg-orange-500/5"
                            : "border-[#2d3748] bg-[#1e2430]"
                        }`}
                      >
                        <div className="flex items-start gap-2.5 min-w-0">
                          <MapPinIcon
                            className={`w-4 h-4 mt-0.5 shrink-0 ${branch.isPrimary ? "text-orange-400" : "text-slate-500"}`}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-200 truncate">
                                {branch.name}
                              </span>
                              {branch.isPrimary && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-500/20 text-orange-400 border border-orange-500/30">
                                  <StarSolid className="w-2.5 h-2.5" />
                                  Primary
                                </span>
                              )}
                            </div>
                            {branch.address?.formattedAddress ? (
                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {branch.address.formattedAddress}
                              </p>
                            ) : branch.address?.city ? (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {[
                                  branch.address.street,
                                  branch.address.city,
                                  branch.address.state,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          {!branch.isPrimary && (
                            <button
                              type="button"
                              onClick={() => setPrimaryBranch(branch._id)}
                              title="Set as primary"
                              className="p-1.5 rounded-md text-slate-500 hover:text-orange-400 hover:bg-[#2d3748] transition"
                            >
                              <StarIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openEditBranch(branch)}
                            title="Edit branch"
                            className="p-1.5 rounded-md text-slate-500 hover:text-orange-400 hover:bg-[#2d3748] transition"
                          >
                            <PencilSquareIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteBranch(branch._id)}
                            title="Delete branch"
                            className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-[#2d3748] transition"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── RELATED CONTACTS (edit mode only) ── */}
            {editingAccount && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Related Contacts
                  </span>
                  <div className="flex-1 h-px bg-[#2d3748]" />
                </div>
                {relatedContacts.length === 0 ? (
                  <p className="text-slate-600 text-sm italic">
                    No contacts linked to this account.
                  </p>
                ) : (
                  <div className="rounded-lg border border-[#2d3748] overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#1a2035] border-b border-[#2d3748]">
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Phone
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#1e2430]">
                        {relatedContacts.map((contact) => (
                          <tr
                            key={contact._id}
                            className="hover:bg-[#1e2430] transition-colors"
                          >
                            <td className="px-4 py-3 text-slate-200">
                              {contact.firstName} {contact.lastName}
                            </td>
                            <td className="px-4 py-3 text-slate-400">
                              {contact.email || (
                                <span className="italic text-slate-600">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-400">
                              {contact.phoneNumber || (
                                <span className="italic text-slate-600">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Form actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={cancelForm}
                className="px-4 py-2 text-sm rounded-lg bg-[#2d3748] text-slate-300 hover:bg-[#374151] transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-sm font-medium rounded-lg bg-orange-500 hover:bg-orange-400 text-white transition shadow-lg shadow-orange-900/20"
              >
                {editingAccount ? "Save Changes" : "Create Account"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── TABLE PANEL ── */}
      <div className="rounded-xl border border-[#2d3748] bg-[#161b27] overflow-hidden shadow-xl">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 px-5 py-4 border-b border-[#2d3748]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by company name…"
                className="w-full pl-9 pr-4 py-2 rounded-lg bg-[#1e2430] border border-[#2d3748] text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <span className="text-xs font-medium text-slate-400">
                  Archived
                </span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => {
                      setShowArchived(e.target.checked);
                      setCurrentPage(1);
                      setTypeFilter("all");
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-[#2d3748] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500" />
                </div>
              </label>
              {!showForm && (
                <button
                  onClick={openAdd}
                  className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-400 text-white rounded-lg text-sm font-medium transition"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add
                </button>
              )}
            </div>
          </div>

          {/* Type filter pills */}
          <div className="flex items-center gap-2">
            {[
              { key: "all", label: "All" },
              { key: "vendor", label: "Vendors" },
              { key: "account", label: "Accounts" },
            ].map(({ key, label }) => {
              const counts = {
                all: displayList.length,
                vendor: displayList.filter((a) => a.type === "vendor").length,
                account: displayList.filter((a) => a.type === "account").length,
              };
              const isActive = typeFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => {
                    setTypeFilter(key);
                    setCurrentPage(1);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                    isActive
                      ? key === "vendor"
                        ? "bg-[#1e3a5f] text-blue-300 border-blue-700"
                        : key === "account"
                          ? "bg-[#1a2e1a] text-green-300 border-green-800"
                          : "bg-orange-500/20 text-orange-400 border-orange-600"
                      : "bg-[#1e2430] text-slate-400 border-[#2d3748] hover:border-slate-500 hover:text-slate-200"
                  }`}
                >
                  {label}
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      isActive
                        ? key === "vendor"
                          ? "bg-blue-900/60 text-blue-300"
                          : key === "account"
                            ? "bg-green-900/60 text-green-300"
                            : "bg-orange-500/30 text-orange-400"
                        : "bg-[#2d3748] text-slate-500"
                    }`}
                  >
                    {counts[key]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Empty state */}
        {filteredAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <BuildingOffice2Icon className="w-12 h-12 opacity-30" />
            <p className="text-sm">
              {searchTerm
                ? "No accounts match your search."
                : showArchived
                  ? "No archived accounts."
                  : "No accounts yet. Add one above."}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#2d3748] bg-[#1a2035]">
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Branches
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e2430]">
                  {currentAccounts.map((account) => (
                    <tr
                      key={account._id}
                      className="hover:bg-[#1e2430] transition-colors group cursor-pointer"
                      onClick={(e) => {
                        if (e.target.closest(".actions-col")) return;
                        editAccount(account);
                      }}
                    >
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <AccountAvatar companyName={account.companyName} />
                          <span className="font-medium text-slate-200">
                            {account.companyName}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            account.type === "vendor"
                              ? "bg-[#1e3a5f] text-blue-300 border-blue-900/40"
                              : "bg-[#1a2e1a] text-green-300 border-green-900/40"
                          }`}
                        >
                          {account.type === "vendor" ? "Vendor" : "Account"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-400">
                        {account.address?.city || (
                          <span className="italic text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {account.branches?.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                            <MapPinIcon className="w-3.5 h-3.5 text-slate-500" />
                            {account.branches.length}{" "}
                            {account.branches.length === 1
                              ? "branch"
                              : "branches"}
                          </span>
                        ) : (
                          <span className="italic text-slate-600 text-xs">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-right actions-col">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              editAccount(account);
                            }}
                            title="Edit"
                            className="p-1.5 rounded-md text-slate-400 hover:text-orange-400 hover:bg-[#2d3748] transition"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          {showArchived ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestore(account._id, account.companyName);
                              }}
                              title="Restore"
                              className="p-1.5 rounded-md text-slate-400 hover:text-green-400 hover:bg-[#2d3748] transition"
                            >
                              <ArchiveBoxArrowDownIcon className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchive(account._id, account.companyName);
                              }}
                              title="Archive"
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-[#2d3748] transition"
                            >
                              <ArchiveBoxIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#2d3748] bg-[#1a2035]">
                <span className="text-xs text-slate-500">
                  {indexOfFirst + 1}–
                  {Math.min(
                    indexOfFirst + itemsPerPage,
                    filteredAccounts.length,
                  )}{" "}
                  of {filteredAccounts.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-[#2d3748] disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-400 px-1">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-[#2d3748] disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
        onConfirm={closeAlert}
        confirmText="OK"
      />
    </div>
  );
}
